/**
 * ParametricAssetManager - Manages parametric asset creation.
 *
 * Provides API for adding predefined engineering assets (bridges, abutments, piers, etc.)
 * with customizable parameters.
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import { selectionService } from "../selection";
import type { CommandHistory } from "../commands/CommandHistory";
import { AddParametricAssetCommand } from "../commands/AddParametricAssetCommand";
import { GeometryFactory } from "../factories/GeometryFactory";
import { applyEntityProps } from "../entities/properties";
import { getEntityHandle } from "../core/oda";
import { ensureLayerExists } from "../layers/utils";
import AssetLibrary from "@/assets/parametric-assets";
import type { Point3 } from "@/types/blocks.types";
import { deleteObj } from "../core";

/** Layer name for parametric assets */
const DR_ASSET_LAYER = "DR_Fond-de-plan";

/** Default color for parametric asset layer */
const DR_ASSET_COLOR = "128, 128, 128";

export class ParametricAssetManager {
  constructor(
    private viewer: DRDrawingViewer,
    private history: CommandHistory,
  ) {}

  /**
   * Add a parametric asset at the canvas center.
   * @param assetId - Asset identifier from ParametricAssetId enum
   * @param values - Parameter values for the asset
   */
  add(assetId: string, values: Record<string, number | boolean>): void {
    if (!this.viewer.database) {
      console.error(
        "[ParametricAssetManager] Cannot add parametric asset: viewer not initialized",
      );
      return;
    }

    // Get the asset definition
    const asset = AssetLibrary.getById(assetId);
    if (!asset) {
      console.warn(
        `[ParametricAssetManager] Unknown parametric asset: ${assetId}`,
      );
      return;
    }

    // Calculate canvas center in world coordinates
    const canvas = this.viewer.canvas;
    const screenCenter = {
      x: canvas.width / 2,
      y: canvas.height / 2,
    };
    const worldCenter = this.viewer.screenToWorld(screenCenter);
    const offset: Point3 = [worldCenter.x, worldCenter.y, 0];

    // Build the geometry using the asset's build function
    const geometries = asset.build(values, offset);

    // Create ODA entities from geometries
    const result = GeometryFactory.createEntities(this.viewer, geometries);

    if (result.entityIds.length === 0) {
      console.warn(
        "[ParametricAssetManager] No entities created for parametric asset",
      );
      return;
    }

    // Ensure the asset layer exists with the correct color
    ensureLayerExists(
      this.viewer.module,
      this.viewer.database,
      DR_ASSET_LAYER,
      DR_ASSET_COLOR,
    );

    // Apply styling to all created entities
    for (const entityId of result.entityIds) {
      applyEntityProps(this.viewer.module, this.viewer.database, entityId, {
        layername: DR_ASSET_LAYER,
        color: "kByLayer",
      });
    }

    // Create and execute command for undo/redo
    const command = new AddParametricAssetCommand(this.viewer, {
      assetId,
      values,
      entityHandles: result.entityIds.map((id) => getEntityHandle(id)),
      originalLayerName: DR_ASSET_LAYER,
    });
    this.history.execute(command);

    // Select the created entities
    // Clear any existing selection first
    selectionService.clear();

    // Add each created entity to the selection
    for (const entityId of result.entityIds) {
      selectionService.add(entityId, false); // Skip grid update during loop
    }

    // Update the transform grid once after adding all entities
    selectionService.updateGrid();

    // Update the view
    this.viewer.update();
    for (const entityId of result.entityIds) {
      deleteObj(entityId);
    }
  }

  /**
   * Get all available parametric asset IDs.
   * @returns Array of asset IDs
   */
  getAvailableAssets(): string[] {
    return AssetLibrary.getAll().map((asset) => asset.id);
  }
}
